import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ExternalLink } from "lucide-react";
import * as React from "react";

export const PYLON_SUPPORT_FORM_URL = "https://portal.usepylon.com/istari-digital/forms/support";

export const PYLON_SUPPORT_IFRAME_LOAD_TIMEOUT_MS = 15_000;

interface SupportRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SupportRequestDialog({ open, onOpenChange }: SupportRequestDialogProps) {
  const [loadState, setLoadState] = React.useState<"loading" | "loaded" | "failed">("loading");

  React.useEffect(() => {
    if (!open) {
      return;
    }

    setLoadState("loading");

    const timeoutId = window.setTimeout(() => {
      setLoadState((current) => (current === "loading" ? "failed" : current));
    }, PYLON_SUPPORT_IFRAME_LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [open]);

  const showFallback = loadState === "failed";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100%-2rem)] flex-col gap-0 p-0 sm:max-w-[640px]">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>Support Request</DialogTitle>
          <DialogDescription>Submit a support request without leaving the portal</DialogDescription>
        </DialogHeader>

        {showFallback ? (
          <div className="flex flex-col items-center gap-4 px-6 py-12 text-center">
            <p className="max-w-md text-sm text-muted-foreground">
              The support form could not be embedded in the portal. Open it in a new tab to continue.
            </p>
            <Button asChild>
              <a href={PYLON_SUPPORT_FORM_URL} target="_blank" rel="noopener noreferrer">
                <ExternalLink />
                Open support form in new tab
              </a>
            </Button>
          </div>
        ) : (
          <div className="relative overflow-hidden">
            {loadState === "loading" && (
              <div className="absolute inset-0 flex items-center justify-center bg-background">
                <p className="text-sm text-muted-foreground">Loading support form...</p>
              </div>
            )}
            <iframe
              title="Support request form"
              src={PYLON_SUPPORT_FORM_URL}
              className={cn(
                "h-[640px] max-h-[calc(90vh-5.5rem)] w-full border-0",
                loadState === "loading" && "opacity-0",
              )}
              onLoad={() => setLoadState("loaded")}
              onError={() => setLoadState("failed")}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
